import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Plus, Trash2, ChevronUp, ChevronDown, Sparkles, AlertTriangle, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

const QTYPES = [["short_text", "qShortText"], ["long_text", "qLongText"], ["yesno", "qYesNo"], ["multiple_choice", "qMultiple"], ["checkbox", "qCheckbox"], ["number", "qNumber"], ["datetime", "qDatetime"], ["photo", "qPhoto"], ["document", "qDocument"]];
const HAS_OPTIONS = ["multiple_choice", "checkbox"];
const uid = () => Math.random().toString(36).slice(2, 10);
const fmtEst = (iso) => { try { return new Date(iso).toLocaleString("en-CA", { timeZone: "America/Montreal", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function ShiftReports() {
  const { t } = useT();
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [editor, setEditor] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchRes, setSearchRes] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTemplates = () => api.get("/shift-reports/templates?all=true").then((r) => setTemplates(r.data));
  const loadReports = () => api.get("/shift-reports/reports").then((r) => setReports(r.data));
  useEffect(() => { loadTemplates(); loadReports(); }, []);

  const newTemplate = () => setEditor({ name: "", department: "", shift: "", questions: [] });
  const editTemplate = (tp) => setEditor({ ...tp, questions: tp.questions.map((q) => ({ ...q })) });
  const addQuestion = () => setEditor((e) => ({ ...e, questions: [...e.questions, { id: uid(), label: "", type: "short_text", required: false, options: [] }] }));
  const setQ = (i, patch) => setEditor((e) => ({ ...e, questions: e.questions.map((q, j) => j === i ? { ...q, ...patch } : q) }));
  const delQ = (i) => setEditor((e) => ({ ...e, questions: e.questions.filter((_, j) => j !== i) }));
  const moveQ = (i, dir) => setEditor((e) => { const qs = [...e.questions]; const j = i + dir; if (j < 0 || j >= qs.length) return e; [qs[i], qs[j]] = [qs[j], qs[i]]; return { ...e, questions: qs }; });

  const saveTemplate = async () => {
    if (!editor.name.trim() || editor.questions.length === 0) { toast.error(t("templateNeedsQuestions")); return; }
    setSaving(true);
    try {
      const body = { name: editor.name, department: editor.department || null, shift: editor.shift || null, questions: editor.questions };
      if (editor.id) await api.put(`/shift-reports/templates/${editor.id}`, body);
      else await api.post("/shift-reports/templates", body);
      toast.success(t("saved"));
      setEditor(null); loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm(`${t("deleteTemplate")} ?`)) return;
    await api.delete(`/shift-reports/templates/${id}`); loadTemplates();
  };

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true); setSearchRes(null);
    try { const { data } = await api.post("/shift-reports/reports/search", { question: query }); setSearchRes(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><ClipboardList className="w-6 h-6 text-primary" />{t("shiftReports")}</h1>
        {tab === "templates" && <Button onClick={newTemplate} data-testid="new-template-btn"><Plus className="w-4 h-4 mr-2" />{t("newTemplate")}</Button>}
      </div>

      <div className="flex gap-1 border-b border-border">
        {[["templates", t("manageTemplates")], ["history", t("reportHistory")]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
        ))}
      </div>

      {tab === "templates" ? (
        <div className="grid gap-3">
          {templates.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">{t("noTemplates")}</p>}
          {templates.map((tp) => (
            <Card key={tp.id} className={`p-4 flex items-center justify-between ${!tp.active ? "opacity-50" : ""}`} data-testid="template-card">
              <div>
                <div className="font-semibold flex items-center gap-2">{tp.name} {!tp.active && <Badge variant="secondary" className="text-[10px]">inactive</Badge>}</div>
                <p className="text-xs text-muted-foreground">{[tp.shift, tp.department].filter(Boolean).join(" · ")}{(tp.shift || tp.department) ? " · " : ""}{tp.questions.length} {t("questions")}</p>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => editTemplate(tp)} data-testid="edit-template"><Pencil className="w-4 h-4" /></Button>
                {tp.active && <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteTemplate(tp.id)} data-testid="delete-template"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <Card className="p-4 border-primary/30 bg-gradient-to-br from-primary/5 to-purple-500/5">
            <p className="text-sm font-semibold flex items-center gap-1.5 mb-2"><Sparkles className="w-4 h-4 text-primary" />{t("searchReports")}</p>
            <div className="flex gap-2">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder={t("searchReports")} data-testid="report-search-input" />
              <Button onClick={runSearch} disabled={searching} data-testid="report-search-btn">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : t("search")}</Button>
            </div>
            {searchRes && <div className="mt-3 text-sm whitespace-pre-wrap" data-testid="report-search-answer">{searchRes.answer}</div>}
          </Card>

          {reports.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">{t("noReports")}</p>}
          {reports.map((r) => (
            <Card key={r.id} className="p-4" data-testid="report-card">
              <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2">{r.template_name}
                    {r.ai_urgent && <Badge className="bg-destructive/15 text-destructive text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />{t("urgent")}</Badge>}</div>
                  <p className="text-xs text-muted-foreground">{fmtEst(r.submitted_at)} EST · {t("submittedBy")} {r.user_name}</p>
                  {r.ai_summary && <p className="text-sm mt-1.5"><span className="text-primary font-medium">{t("aiSummary")}: </span>{r.ai_summary}</p>}
                </div>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${expanded === r.id ? "rotate-180" : ""}`} />
              </div>
              {expanded === r.id && (
                <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  {r.answers.map((a, i) => (
                    <div key={i} className="text-sm">
                      <span className="text-muted-foreground">{a.label}: </span>
                      <span className="font-medium">{Array.isArray(a.value) ? a.value.join(", ") : (a.type === "photo" || a.type === "document" ? `${(a.attachments || []).length} ${t("files")}` : (a.value || "—"))}</span>
                    </div>
                  ))}
                  {r.ai_missing?.length > 0 && (
                    <div className="mt-2 text-xs text-amber-600"><b>{t("missingInfo")}:</b> {r.ai_missing.join("; ")}</div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* template builder */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editor?.id ? t("editTemplate") : t("newTemplate")}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <Input placeholder={t("templateName")} value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="template-name-input" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder={t("shift")} value={editor.shift || ""} onChange={(e) => setEditor({ ...editor, shift: e.target.value })} />
                <Input placeholder={t("department")} value={editor.department || ""} onChange={(e) => setEditor({ ...editor, department: e.target.value })} />
              </div>
              <div className="space-y-2">
                {editor.questions.map((q, i) => (
                  <Card key={q.id} className="p-3 space-y-2" data-testid="question-row">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                      <Input placeholder={t("questionText")} value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} className="flex-1" data-testid="question-label" />
                      <Button size="icon" variant="ghost" onClick={() => moveQ(i, -1)}><ChevronUp className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => moveQ(i, 1)}><ChevronDown className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delQ(i)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap pl-7">
                      <Select value={q.type} onValueChange={(v) => setQ(i, { type: v })}>
                        <SelectTrigger className="h-8 w-40" data-testid="question-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{QTYPES.map(([val, key]) => <SelectItem key={val} value={val}>{t(key)}</SelectItem>)}</SelectContent>
                      </Select>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="checkbox" checked={q.required} onChange={(e) => setQ(i, { required: e.target.checked })} data-testid="question-required" />{t("required")}
                      </label>
                      {HAS_OPTIONS.includes(q.type) && (
                        <Input placeholder={t("options")} value={(q.options || []).join(", ")} onChange={(e) => setQ(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="h-8 flex-1 min-w-[160px]" data-testid="question-options" />
                      )}
                    </div>
                  </Card>
                ))}
                <Button variant="outline" size="sm" onClick={addQuestion} data-testid="add-question-btn"><Plus className="w-4 h-4 mr-2" />{t("addQuestion")}</Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditor(null)}>{t("cancel")}</Button>
                <Button onClick={saveTemplate} disabled={saving} data-testid="save-template-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{t("saveTemplate")}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
